import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ApproveEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/ApproveEnrollmentModal'
import DropEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/DropEnrollmentModal'
import RefundEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/RefundEnrollmentModal'
import SuccessModal from '../../common/customModal/SuccessModal'
import ConfirmActionModal from './ConfirmActionModal'
import RefundInvoiceModal from './RefundInvoiceModal'

// RefundEnrollmentModal predates E&B and reads the roster's enrollment shape (`user`, `paid_at`),
// while the drawer loads the E&B shape (`student`, `paidAt`). Adapt here rather than touching the
// shared modal, so the roster flow is unaffected.
const legacyRefundEnrollment = enrollment => ({
  ...enrollment,
  user: enrollment.student,
  paid_at: enrollment.paidAt,
  // The serializer omits fees until the Stripe payment entry lands; the modal indexes into it.
  fees: enrollment.fees || {},
})

// Action modals (approve / drop / delete / refund) for one enrollment. Driven entirely by props, so
// both the Enrollments & Billing drawer and the Members drawer render it from the prop bundle
// useEnrollmentActions returns.
const EnrollmentActionModals = ({
  activeModal, closeModal, coachMessage, setCoachMessage, notifyViaEmail, toggleNotify,
  isLoading, onApprove, onDrop, onDropNoRefund, onDropRefund, onRemove, dropEnrollment, invoice, dropSuccessTitle, closeDropSuccess,
}) => {
  const { t } = useTranslation()
  return (
    <>
      {activeModal === 'approve' && (
        <ApproveEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleApproveEnrollment={onApprove}
          isLoading={isLoading}
        />
      )}

      {(activeModal === 'drop' || activeModal === 'remove') && (
        <DropEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleRemoveEnrollment={activeModal === 'drop' ? onDrop : onRemove}
          enrollment={dropEnrollment}
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={toggleNotify}
          isLoading={isLoading}
        />
      )}

      {/* Dropping a paid enrollment that has an R3 invoice: refund (full or partial) against it. */}
      {activeModal === 'dropRefund' && dropEnrollment && (
        <RefundInvoiceModal
          isOpen
          mode='dropRefund'
          onClose={closeModal}
          onSubmit={onDropRefund}
          isLoading={isLoading}
          enrollment={dropEnrollment}
          invoice={invoice}
        />
      )}

      {/* Paid online with no invoice: the pre-invoicing modal — drop with a full refund, or without one. */}
      {activeModal === 'dropRefundLegacy' && dropEnrollment && (
        <RefundEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleRemoveEnrollment={refund => (refund ? onDrop() : onDropNoRefund())}
          enrollment={legacyRefundEnrollment(dropEnrollment)}
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={toggleNotify}
          isLoading={isLoading}
        />
      )}

      {activeModal === 'delete' && (
        <ConfirmActionModal
          isOpen
          onClose={closeModal}
          onConfirm={onRemove}
          isLoading={isLoading}
          title={t('enrollmentsBilling.drawer.deleteEnrollment')}
          message={t('enrollmentsBilling.drawer.deleteEnrollmentMessage')}
          confirmMessage={t('enrollmentsBilling.drawer.deleteEnrollment')}
          cancelMessage={t('enrollmentsBilling.drawer.exit')}
        />
      )}

      <SuccessModal
        isOpen={!!dropSuccessTitle}
        onClose={closeDropSuccess}
        title={dropSuccessTitle}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
    </>
  )
}

EnrollmentActionModals.propTypes = {
  activeModal: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  coachMessage: PropTypes.string,
  setCoachMessage: PropTypes.func.isRequired,
  notifyViaEmail: PropTypes.bool,
  toggleNotify: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDropNoRefund: PropTypes.func.isRequired,
  onDropRefund: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  dropEnrollment: PropTypes.object,
  invoice: PropTypes.object,
  dropSuccessTitle: PropTypes.string,
  closeDropSuccess: PropTypes.func.isRequired,
}

export default EnrollmentActionModals
